import { useSyncExternalStore } from 'react';
import { shapesStore } from '../collections/diagramCollections';

// Subscribers for shape changes
const shapeSubscribers = new Set<() => void>();

// Notify all subscribers when shapes change
export function notifyShapeChange() {
  shapeSubscribers.forEach((callback) => callback());
}

/**
 * Hook for all visible shapes in the diagram
 * Reactive - re-renders when shapes change
 */
export function useDiagramShapes() {
  return useSyncExternalStore(
    (callback) => {
      shapeSubscribers.add(callback);
      return () => shapeSubscribers.delete(callback);
    },
    () => {
      const shapes = Array.from(shapesStore.values())
        .filter((shape) => shape.visible !== false)
        .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
      return shapes;
    }
  );
}

/**
 * Hook for a single shape by ID
 * Reactive - updates instantly when shape changes
 */
export function useDiagramShape(shapeId: string | null) {
  return useSyncExternalStore(
    (callback) => {
      shapeSubscribers.add(callback);
      return () => shapeSubscribers.delete(callback);
    },
    () => {
      if (!shapeId) return null;
      return shapesStore.get(shapeId) || null;
    }
  );
}

/**
 * Hook for selected shapes
 * Useful for multi-selection operations
 */
export function useSelectedShapes(selectedIds: string[]) {
  return useSyncExternalStore(
    (callback) => {
      shapeSubscribers.add(callback);
      return () => shapeSubscribers.delete(callback);
    },
    () => {
      if (selectedIds.length === 0) return [];
      
      return Array.from(shapesStore.values())
        .filter((shape) => selectedIds.includes(shape.id));
    }
  );
}

